import { CreateTopicEmailGateRequest, CreateTopicGateRequest, CreateTopicPasswordGateRequest, CreateTopicPaymentGateRequest, DiscoveryLevel, FeatureType, GetGatedAccessEventResponse, GetPointResponse, GetTopicGateResponse, ObjectId, TopicPermissionLevel } from '../../../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, queryAsync, state } from 'lit/decorators';
import { delay, getGatedAccessData, isDefined } from '../../../helpers/utils';

import { Dialog } from '@material/mwc-dialog';
import { ErrorToastElement } from '../../error-toast';
import { SettingDrawer } from '../../mobile/setting-drawer';
import { detectMobileViewport } from '../../../helpers/device';
import featuresInfo from '../../../helpers/state/features-info';
import { getAllPointsPaginated } from '../../../helpers/api/points-api';
import { getMainPage } from '../../../helpers/page-name';
import { logWithAmplitude } from '../../../helpers/api/api';
import pointCache from '../../../helpers/state/point-cache';
import topicData from '../../../helpers/state/topic-data';

const CHARGE_ID = 'Paywall-Access-USD';

@customElement('admin-gated-access-addon')
export class AdminGatedAccessAddon extends LitElement {
    amplitude_callsite: string = 'Gated Access Add On';

    @query('#errorToast')
    errorToastElement?: ErrorToastElement;

    @queryAsync('#errorToast')
    errorToastElementAsync?: Promise<ErrorToastElement>;

    @query('#selectPointsDrawer')
    selectPointsDrawer?: SettingDrawer;

    @query('#selectPointsModal')
    selectPointsModal?: Dialog;

    @property({type: Array})
    accesses: GetGatedAccessEventResponse[] = [];

    @state()
    selectedTab: string = 'password';

    @state()
    showInsight: boolean = false;

    @state()
    isEditMode: boolean = false;

    @state()
    editingId: string | undefined = undefined;

    @state()
    isPasswordsOn: boolean = false;

    @state()
    isPaywallsOn: boolean = false;

    @state()
    isEmailGateOn: boolean = false;

    @state()
    password: string = '';

    @state()
    passwordName: string = '';

    @state()
    paymentName: string = '';

    @state()
    price: number = 0;

    @state()
    currency: string = 'USD';

    @state()
    requireEmailVerification: boolean = false;

    @state()
    requireOptInCheckbox: boolean = false;

    @state()
    optInLanguage: string = '';

    @state()
    previewedPoints: GetPointResponse[] = [];

    @state()
    totalEarned: number = 0;

    @state()
    isSaving: boolean = false;

    async firstUpdated() {
        // wait until topic data is ready
        while (!topicData.isReady || !pointCache.isReady) {
            await delay(100);
        }
        this.isPasswordsOn = topicData.topic?.gates?.some((gate: CreateTopicGateRequest) => {
            const passwordGate = gate as CreateTopicPasswordGateRequest;
            return isDefined(passwordGate.Password) && passwordGate.Password.is_active;
        }) ?? false;
        this.isPaywallsOn = topicData.topic?.gates?.some((gate: CreateTopicGateRequest) => {
            const paymentGate = gate as CreateTopicPaymentGateRequest;
            return isDefined(paymentGate.Payment) && paymentGate.Payment.is_active;
        }) ?? false;
        this.isEmailGateOn = topicData.topic?.gates?.some((gate: CreateTopicGateRequest) => {
            const emailGate = gate as CreateTopicEmailGateRequest;
            return isDefined(emailGate.Email) && emailGate.Email.is_active;
        }) ?? false;
        this.previewedPoints = pointCache.allPoints.filter((point) => !point.is_gate_hidden);

        // if we are on email, set the first email gate as the editing gate or create a new one if none exists
        if (this.selectedTab === 'email') {
            const emailGate = topicData.topic?.gates?.find((gate: GetTopicGateResponse) => {
                const emailGate = gate as CreateTopicEmailGateRequest;
                return isDefined(emailGate.Email);
            });
            if (isDefined(emailGate) && isDefined((emailGate as CreateTopicEmailGateRequest).Email)) {
                const gate = emailGate as CreateTopicEmailGateRequest;
                this.editingId = gate.Email?._id?.$oid;
                this.optInLanguage = gate.Email?.opt_in_language ?? '';
                this.requireEmailVerification = gate.Email?.require_verification ?? false;
                this.requireOptInCheckbox = gate.Email?.require_opt_in ?? false;
            } else {
                await this.createEmailGate();
            }
        }

        try{
            await this.getAccesses();
        } catch (error) {
            getMainPage().errorToastElement?.showToastAndThrow(
                'Error getting accesses.' + (error as Error).message
            );
        }
    }

    private async createEmailGate() {
        const gates: Array<CreateTopicGateRequest | GetTopicGateResponse> = topicData.topic?.gates ?? [];
        const newGate: CreateTopicEmailGateRequest = {
            Email: {
                _id: undefined,
                name: 'Email Gate',
                is_active: false,
                require_opt_in: true,
                require_verification: true,
                opt_in_language: '',
                level: TopicPermissionLevel.READER,
            }
        };
        gates.push(newGate);
        try {
            await topicData.fetchAndUpdateTopicData({gates}, this.amplitude_callsite);
        } catch (error) {
            getMainPage().errorToastElement?.showToastAndThrow(
                'Error creating email gate.' + (error as Error).message
            );
        } finally {
            await this.firstUpdated();
        }
    }

    private async togglePasswordProtection(toggleAll: boolean, newStatus: boolean, id?: string) {
        if (toggleAll) {
            this.isPasswordsOn = newStatus;
        }
        if (newStatus && !this.isPasswordsOn) {
            this.isPasswordsOn = true;
        }
        const gates = topicData.topic?.gates?.map((gate) => {
            const gatePass = (gate as CreateTopicPasswordGateRequest);
            return {
                ...gate,
                ...(isDefined(gatePass.Password) && {
                    Password: {
                        ...gatePass.Password,
                        is_active: toggleAll
                            ? !this.isPasswordsOn ? false : gatePass.Password.is_active
                            : gatePass.Password._id?.$oid === id ? newStatus : gatePass.Password.is_active,
                    }
                })
            };
        });
        try {
            await topicData.fetchAndUpdateTopicData({gates}, this.amplitude_callsite);
        } catch (error) {
            getMainPage().errorToastElement?.showToastAndThrow(
                'Error toggling password protection.' + (error as Error).message
            );
        } finally {
            await this.firstUpdated();
        }
    }

    private async toggleEmailGateProtection(newStatus: boolean) {
        this.isEmailGateOn = newStatus;
        const gates = topicData.topic?.gates?.map((gate) => {
            const gateEmail = (gate as CreateTopicEmailGateRequest);
            return {
                ...gate,
                ...(isDefined(gateEmail.Email) && {
                    Email: {
                        ...gateEmail.Email,
                        is_active: this.isEmailGateOn,

                    }
                })
            };
        });
        try {
            await topicData.fetchAndUpdateTopicData({gates}, this.amplitude_callsite);
        } catch (error) {
            getMainPage().errorToastElement?.showToastAndThrow(
                'Error toggling email gate protection.' + (error as Error).message
            );
        } finally {
            await this.firstUpdated();
        }
    }

    private async copyText(text: string) {
        await navigator.clipboard.writeText(text);

        getMainPage().errorToastElement?.showToast('Copied Password to clipboard.');

        logWithAmplitude(this.amplitude_callsite, 'Copied Password', {
            topic_id: topicData.topicId,
        });
    }

    private async copyPaymentLink(gate_id: ObjectId) {
        // create url with topic_id, topic key and gate_id

        let link = '';
        if (topicData.topic?.discovery_level === DiscoveryLevel.Private && topicData.topic?.cc_read_link !== undefined) {
            link = topicData.topic?.cc_read_link;
        } else if (topicData.topic?.cc_keyless_link !== undefined) {
            link = topicData.topic?.cc_keyless_link;
        }

        if (link === '' || link === undefined) {
            getMainPage().errorToastElement?.showToastAndThrow('Error getting payment link.');
            return;
        }

        // workaround to append gate_id to redirectURL clean up when aaron changes back end to accept whole parameter string
        const readurl = link;
        const gate_id_string = `${gate_id.$oid}`;
        const encoded_gate_id = encodeURIComponent(gate_id_string);
        const url = `${readurl}?gate_id=${encoded_gate_id}`;
        await navigator.clipboard.writeText(url);

        getMainPage().errorToastElement?.showToast('Copied Payment Link to clipboard.');

        logWithAmplitude(this.amplitude_callsite, 'Copied Payment Link', {
            topic_id: topicData.topicId,
        });
    }

    private async getAccesses() {
        const accessData = await getGatedAccessData(this.amplitude_callsite, topicData.topicId, topicData.topicKey);
        this.accesses = accessData.accesses;
        this.totalEarned = accessData.total_earned;
    }

    private changeTab(tab: string) {
        this.selectedTab = tab;
        this.isEditMode = false;
    }

    private clickSeeInsight() {
        this.showInsight = true;
    }

    private onBack() {
        this.showInsight = false;
    }

    private async clickPreview() {
        // send event to parent that its time to preview
        this.dispatchEvent(new CustomEvent('on-preview'));
    }

    private clickEdit(id?: string) {
        const editableGate = topicData.topic?.gates?.find((gate: CreateTopicGateRequest) =>
            (gate as CreateTopicPasswordGateRequest).Password?._id?.$oid === id ||
            (gate as CreateTopicPaymentGateRequest).Payment?._id?.$oid === id
        );
        if (isDefined(editableGate) && isDefined((editableGate as CreateTopicPasswordGateRequest).Password)) {
            this.password = (editableGate as CreateTopicPasswordGateRequest).Password?.password ?? '';
            this.passwordName = (editableGate as CreateTopicPasswordGateRequest).Password?.name ?? '';
            this.editingId = id;
        } else {
            this.password = '';
            this.passwordName = '';
            this.paymentName = '';
            this.price = 0;
            this.editingId = undefined;
        }
        this.isEditMode = true;

        logWithAmplitude(this.amplitude_callsite, 'Edit Password or Paywall', {
            topic_id: topicData.topicId,
            password_or_paywall_id: id,
        });
    }

    onChangePassword(e: CustomEvent) {
        this.password = (e.target as HTMLInputElement).value;
    }

    onChangePasswordName(e: CustomEvent) {
        this.passwordName = (e.target as HTMLInputElement).value;
    }

    onChangePaymentName(e: CustomEvent) {
        this.paymentName = (e.target as HTMLInputElement).value;
    }

    onChangePrice(e: CustomEvent) {
        const price = (e.target as HTMLInputElement).value;
        this.price = isDefined(price) ? parseInt(price, 10) : 0;
    }

    private async savePassword() {
        if (this.password.length < 4) {
            getMainPage().errorToastElement?.showToastAndThrow('Password must be at least 4 characters.');
            return;
        }
        const gates: Array<GetTopicGateResponse| CreateTopicGateRequest>= topicData.topic?.gates ?? [];
        const newGate: CreateTopicGateRequest = {
            Password: {
                name: this.passwordName ?? '',
                password: this.password ?? '',
                level: TopicPermissionLevel.READER,
                is_active: true,
            }
        };

        if (this.passwordName && this.password) {
            const editingGateIndex = gates.findIndex((gate: CreateTopicGateRequest) =>
                (gate as CreateTopicPasswordGateRequest).Password?._id?.$oid === this.editingId
            );
            if (editingGateIndex >= 0 && this.editingId) {
                gates[editingGateIndex] = newGate;
            } else {
                gates.push(newGate);
            }
            try {
                await topicData.fetchAndUpdateTopicData({gates}, this.amplitude_callsite);
                this.isEditMode = false;
                this.editingId = undefined;
            } catch (error) {
                getMainPage().errorToastElement?.showToastAndThrow(
                    'Error saving password protection.' + (error as Error).message
                );
            } finally {
                await this.firstUpdated();
            }
        }
    }

    private async savePaywall() {
        this.isSaving = true;
        getMainPage().errorToastElement.showToast('Saving paywall settings... this may take a moment.');
        const gates: Array<CreateTopicGateRequest | GetTopicGateResponse> = topicData.topic?.gates ?? [];

        if (this.paymentName) {
            const editingGateIndex = gates.findIndex((gate: CreateTopicGateRequest) =>
                (gate as CreateTopicPaymentGateRequest).Payment?._id?.$oid === this.editingId
            );

            if (editingGateIndex >= 0 && this.editingId) {
                // Update existing gate instead of replacing it entirely
                const existingGate = gates[editingGateIndex] as CreateTopicPaymentGateRequest;
                gates[editingGateIndex] = {
                    ...existingGate,
                    Payment: {
                        ...existingGate.Payment,
                        _id: existingGate.Payment._id,
                        name: this.paymentName,
                        price_in_cents: Number(this.price ?? 0) * 100,
                        charge_id: CHARGE_ID,
                        level: TopicPermissionLevel.READER,
                        is_active: true,
                    }
                };
            } else {
                // Create new gate
                const newGate: CreateTopicGateRequest = {
                    Payment: {
                        name: this.paymentName ?? '',
                        price_in_cents: Number(this.price ?? 0) * 100,
                        charge_id: CHARGE_ID,
                        level: TopicPermissionLevel.READER,
                        is_active: true,
                    }
                };
                gates.push(newGate);
            }

            try {
                await topicData.fetchAndUpdateTopicData({gates}, this.amplitude_callsite);
                this.isEditMode = false;
                this.editingId = undefined;
            } catch (error) {
                this.isSaving = false;
                getMainPage().errorToastElement?.showToastAndThrow(
                    'Error saving paywall protection.' + (error as Error).message
                );
            } finally {
                this.isSaving = false;
                await this.firstUpdated();
            }
        }
    }

    private async closeSelectPointsModal() {
        if (detectMobileViewport()) {
            if (this.selectPointsDrawer?.isOpened === true) {
                await this.selectPointsDrawer?.close();
            }
        } else if (isDefined(this.selectPointsModal)) {
            if (this.selectPointsModal.open) {
                this.selectPointsModal.close();
            }
        }
        const newPointsSinceLastUpdate = await getAllPointsPaginated(
            this.amplitude_callsite,
            topicData.topicId,
            topicData.topicKey,
            undefined,
            false
        );
        this.previewedPoints = newPointsSinceLastUpdate?.filter((point) => !point.is_gate_hidden);
    }

    private async openSelectPointsModal() {
        if (detectMobileViewport()) {
            if (this.selectPointsDrawer?.isOpened !== true) {
                await this.selectPointsDrawer?.open();
            }
        } else if (isDefined(this.selectPointsModal)) {
            if (!this.selectPointsModal.open) {
                this.selectPointsModal.show();
            }
        }
    }

    render() {
        return html`
            ${this.showInsight
                ? html`
                    <gated-access-insights-component
                        @on-back="${this.onBack}"
                        .selectedTab="${this.selectedTab}"
                    ></gated-access-insights-component>
                `
                : html`
                    <div id="container" class="addon__container">
                        <div class="main-wrapper">
                            <h4 class="sub-title">
                                Select Preview Points
                            </h4>
                            <p class="info-paragraph">
                                If desired, you can select points that will be available for preview to users who have not gained access to your map. This helps them learn what the content on the map may look like.
                            </p>
                            <mwc-button
                                raised
                                class="assign-preview-points"
                                ?disabled="${!(featuresInfo.paywallEnabled || featuresInfo.passwordEnabled || featuresInfo.emailProtectedEnabled)}"
                                @click="${this.openSelectPointsModal}"
                            >
                                Select points available for preview
                            </mwc-button>
                            <p class="info-paragraph">
                                Currently, <b>${pointCache.allPoints?.filter(
                                    (point) => !point.is_gate_hidden
                                ).length}</b> of your <b>${pointCache.allPoints.length} </b> points are available for the public to view.
                            </p>
                            <br>
                            <h4 class="sub-title">
                                ${this.selectedTab === 'password'
                                    ? html`Password Gate ${!featuresInfo.passwordEnabled ? html`<upgrade-cta iconSize="large" feature="${FeatureType.PasswordProtection}"></upgrade-cta>` : ''}`
                                    : this.selectedTab === 'paywall' ?
                                        html`Paywall Gate ${!featuresInfo.paywallEnabled ? html`<upgrade-cta iconSize="large" feature="${FeatureType.PaywallProtection}"></upgrade-cta>` : ''}`
                                    : html`Email Gate ${!featuresInfo.emailProtectedEnabled ? html`<upgrade-cta iconSize="large" feature="${FeatureType.EmailProtection}"></upgrade-cta>` : ''}`
                                }
                            </h4>
                            <p class="info-paragraph">
                                ${this.selectedTab === 'password'
                                    ? 'You can protect your map with one or multiple passwords. Only users with passwords will be able to view the map. Make sure to not reuse your personal passwords!'
                                    : this.selectedTab === 'paywall'
                                    ? 'Get paid to do what you love. Set up a paywall with custom pricing options and track revenue generated through paywalls.'
                                    : 'Protect your map with an email barrier. User will need to enter their email to access the map.'
                                }
                            </p>
                            ${this.selectedTab === 'password'
                                ? html`
                                    <div class="setting-switch">
                                        <label
                                            class="switch-label left"
                                            for="password-protection"
                                        >
                                            Passwords Off
                                        </label>
                                        <form-switch
                                            class="switch-icon"
                                            id="password-protection"
                                            name="password-protection"
                                            .checked=${this.isPasswordsOn}
                                            ?disabled="${!featuresInfo.passwordEnabled}"
                                            @change=${async (e: any) => await this.togglePasswordProtection(true, e.detail)}
                                        >
                                        </form-switch>
                                        <label
                                            class="switch-label"
                                            for="password-protection"
                                        >
                                            Passwords On
                                        </label>
                                    </div>
                                `
                                :  this.selectedTab === 'paywall' ?
                                    html`

                                    `
                                : html`
                                    <div class="setting-switch">
                                        <label
                                            class="switch-label left"
                                            for="email-protection"
                                        >
                                            Email Gate Off
                                        </label>
                                        <form-switch
                                            class="switch-icon"
                                            id="email-protection"
                                            name="email-protection"
                                            .checked=${this.isEmailGateOn}
                                            ?disabled="${!featuresInfo.emailProtectedEnabled}"
                                            @change=${async (e: any) => await this.toggleEmailGateProtection(e.detail)}
                                        >
                                        </form-switch>
                                        <label
                                            class="switch-label"
                                            for="email-protection"
                                        >
                                            Email Gate On
                                        </label>
                                    </div>
                                `
                            }
                            <h4 class="sub-title" style="margin-bottom: 10px">
                                ${this.selectedTab === 'password'
                                    ? 'Create & Manage Passwords'
                                    : this.selectedTab === 'paywall'
                                        ? html`Paywall Settings<br>Total Earned: $${this.totalEarned} USD`
                                    : 'Email Gate Settings'
                                }
                            </h4>
                            ${this.selectedTab === 'password'
                                ? this.isEditMode
                                    ? html`
                                        <mwc-textfield
                                            outlined
                                            required
                                            class="edit__textfield"
                                            id="password"
                                            type="text"
                                            label="Password"
                                            placeholder="Password"
                                            value="${this.password ?? ''}"
                                            @change="${this.onChangePassword}"
                                        >
                                        </mwc-textfield>
                                        <br>
                                        <mwc-textfield
                                            outlined
                                            required
                                            class="edit__textfield"
                                            id="password-name"
                                            type="text"
                                            label="Password Name or Identifier (Internal Use)"
                                            placeholder="Name (Internal Use Only)"
                                            value="${this.passwordName ?? ''}"
                                            @change="${this.onChangePasswordName}"
                                        >
                                        </mwc-textfield>
                                    `
                                    : html`
                                        <div class="item-list">
                                            <div class="item-list__row">
                                                <div class="item-row__status">
                                                    <span class="item-row__header">
                                                        Active?
                                                        <form-tooltip
                                                            message="Activate or deactivate a ${this.selectedTab === 'password' ? 'password': 'paywall'} here. Deletion is not supported at this time."
                                                            position="top"
                                                            tipwidth="100px"
                                                            >
                                                            <mwc-icon class="info-icon">info</mwc-icon>
                                                        </form-tooltip>
                                                    </span>
                                                </div>
                                                <div class="item-row__name">
                                                    <span class="item-row__header">
                                                        Name
                                                    </span>
                                                </div>
                                                <div class="item-row__copy">
                                                    <span class="item-row__header">
                                                        Copy Password
                                                    </span>
                                                </div>
                                                <div class="item-row__edit">
                                                    <span class="item-row__header">
                                                        Edit
                                                    </span>
                                                </div>
                                                <div class="item-row__uses">
                                                    <span class="item-row__header">
                                                        Accesses
                                                    </span>
                                                </div>
                                            </div>
                                            ${topicData.topic?.gates?.map((gate: CreateTopicGateRequest) => {
                                                const passwordGate = gate as CreateTopicPasswordGateRequest;
                                                if (!isDefined(passwordGate.Password)) return '';
                                                return html`
                                                    <div class="item-list__row">
                                                        <div class="item-row__status">
                                                            <form-switch
                                                                class="switch-icon"
                                                                .disabled=${!featuresInfo.passwordEnabled}
                                                                .checked="${passwordGate.Password?.is_active}"
                                                                @change=${async (e: any) => await this.togglePasswordProtection(false, e.detail, passwordGate.Password?._id?.$oid)}
                                                            >
                                                            </form-switch>
                                                        </div>
                                                        <div class="item-row__name">
                                                            <span class="item-row__label">
                                                                ${passwordGate.Password?.name ?? ''}
                                                            </span>
                                                        </div>
                                                        <div class="item-row__copy" @click="${async () => await this.copyText(passwordGate.Password?.password ?? '')}">
                                                            <mwc-icon class="item-row__action">content_copy</mwc-icon>
                                                        </div>
                                                        <div class="item-row__edit">
                                                            <mwc-icon
                                                                class="item-row__action"
                                                                @click="${() => this.clickEdit(passwordGate.Password?._id?.$oid ?? '')}"
                                                            >edit</mwc-icon>
                                                        </div>
                                                        <div class="item-row__uses">
                                                            <span class="item-row__label">
                                                                ${this.accesses.filter((access) => access.gate_id.$oid === passwordGate.Password?._id?.$oid).length}
                                                            </span>
                                                        </div>
                                                    </div>
                                                `;
                                            })}

                                        </div>
                                    `
                                : this.selectedTab === 'paywall' ?
                                            '' :
                                this.renderEmailGateSettings()
                            }
                        </div>
                    </div>

                    <div class="addon__footer">
                        ${this.selectedTab === 'password' ? html`
                            ${!this.isEditMode ? html`
                                <div
                                    class="item-add-button"
                                    @click="${this.clickEdit}"
                                >
                                    <mwc-icon class="item-add__icon">add</mwc-icon>
                                    <span class="item-add__label">Add Password</span>
                                </div>
                            ` : html`
                                <div class="action-buttons">
                                    <div
                                        class="item-add-button"
                                        @click="${() => { this.isEditMode = false; this.editingId = undefined; }}"
                                    >
                                        <mwc-icon class="item-add__icon">arrow_back</mwc-icon>
                                        <span class="item-add__label">Return</span>
                                    </div>
                                    <div
                                        class="item-add-button"
                                        @click="${this.savePassword}"
                                    >
                                        <mwc-icon class="item-add__icon">save</mwc-icon>
                                        <span class="item-add__label">Save Password Changes</span>
                                    </div>
                                </div>
                            `}
                        ` : this.selectedTab === 'paywall' ?
                            html`
                                ${!this.isEditMode ? html`
                                    <div
                                        class="item-add-button"
                                        @click="${this.clickEdit}"
                                    >
                                        <mwc-icon class="item-add__icon">add</mwc-icon>
                                        <span class="item-add__label">Add Payment Gate</span>
                                    </div>
                                ` : html`
                                    <div class="action-buttons">
                                        <div
                                            class="item-add-button"
                                            @click="${() => { this.isEditMode = false; this.editingId = undefined; }}"
                                        >
                                            <mwc-icon class="item-add__icon">arrow_back</mwc-icon>
                                            <span class="item-add__label">Return</span>
                                        </div>
                                        <div
                                            class="item-add-button"
                                            ?disabled="${this.isSaving}"
                                            @click="${this.savePaywall}"
                                        >
                                            <mwc-icon class="item-add__icon">save</mwc-icon>
                                            <span class="item-add__label">Save Pricing Changes</span>
                                        </div>
                                    </div>
                                `}
                        ` : ''
                        }
                        <div class="buttons-wrapper">
                            <form-button
                                size="md"
                                variant="outline"
                                class=""
                                @click="${this.clickSeeInsight}"
                            >
                                See Insights
                            </form-button>

                            <form-button
                                size="md"
                                @click="${this.clickPreview}"
                            >
                                Preview
                            </form-button>
                        </div>
                    </div>
                    ${detectMobileViewport()
                        ? html`
                            <setting-drawer
                                id="selectPointsDrawer"
                                @on-close="${this.closeSelectPointsModal}">
                                <gated-access-select-points-modal
                                    .selectedPoints=${this.previewedPoints}
                                    @close="${this.closeSelectPointsModal}">
                                </gated-access-select-points-modal>
                            </setting-drawer>
                        `
                        : html`
                            <mwc-dialog
                                id="selectPointsModal"
                                class="dialog edit__dialog"
                                hideActions
                                scrimClickAction=""
                                escapeKeyAction=""
                            >
                                <modal-dialog
                                    size="md"
                                    @close="${this.closeSelectPointsModal}">

                                    <gated-access-select-points-modal
                                        slot="body"
                                        .selectedPoints=${this.previewedPoints}
                                        @close="${this.closeSelectPointsModal}">
                                    </gated-access-select-points-modal>
                                </modal-dialog>
                            </mwc-dialog>
                        `
                    }
                `
            }
            <error-toast id="errorToast"></error-toast>
        `;
    }

    // Email gate has options to require email verification, add opt in checkbox, and option to require opt in checkbox to be checked
    renderEmailGateSettings() {
        return html`

            <div class="checkbox-wrapper">
                <form-checkbox ?disabled="${!featuresInfo?.emailProtectedEnabled}"
                    id="requireEmailVerification"
                    name="requireEmailVerification"
                    class_name="blue"
                    ?checked="${this.requireEmailVerification}"
                    @change="${async (e: any) => {this.requireEmailVerification = (e.detail as HTMLInputElement).checked; await this.updateEmailGateRequireEmailVerification();}}"
                    >
                </form-checkbox>
                <span>Require users to verify their email before accessing the map. Proxi will send them a 6-digit code for them to enter.</span>
            </div>
            <div class="checkbox-wrapper">
                <form-checkbox ?disabled="${!featuresInfo?.emailProtectedEnabled}"
                    id="addOptInCheckbox"
                    name="addOptInCheckbox"
                    class_name="blue"
                    ?checked="${this.optInLanguage !== ''}"
                    @change="${async (e: any) => {(e.detail as HTMLInputElement).checked ? this.optInLanguage = 'I agree to the terms and conditions' : this.optInLanguage = ''; await this.updateEmailGateOptInLanguage(); await this.updateEmailGateRequireOptInCheckbox();}}"
                    >
                </form-checkbox>
                <span>Add an opt-in checkbox for users to agree to your terms and conditions before accessing the map.</span>
            </div>
            ${this.optInLanguage !== '' ? html`
                <div class="checkbox-wrapper">
                    <form-checkbox ?disabled="${!featuresInfo?.emailProtectedEnabled}"
                        id="requireOptInCheckbox"
                        name="requireOptInCheckbox"
                        class_name="blue"
                        ?checked="${this.requireOptInCheckbox}"
                        @change="${async (e: any) => {this.requireOptInCheckbox = (e.detail as HTMLInputElement).checked; await this.updateEmailGateRequireOptInCheckbox();}}"
                        >
                    </form-checkbox>
                    <span>Require users to check the opt-in checkbox before accessing the map.</span>
                </div>
                <div class="opt-in-text">
                    <proxi-textarea
                        id="optInText"
                        outlined
                        name="optInText"
                        label="Opt-In Language"
                        class="opt-in-text__textarea"
                        ?disabled="${!featuresInfo?.emailProtectedEnabled}"
                        .value="${this.optInLanguage}"
                        @change="${async (e: any) => {this.optInLanguage = e.target.value; await this.updateEmailGateOptInLanguage();}}"
                    >
                    </proxi-textarea>
                </div>
            ` : ''}
        `;
    }

    async updateEmailGateOptInLanguage() {
        const gates = topicData.topic?.gates?.map((gate) => {
            const gateEmail = (gate as CreateTopicEmailGateRequest);
            return {
                ...gate,
                ...(isDefined(gateEmail.Email) && {
                    Email: {
                        ...gateEmail.Email,
                        opt_in_language: this.optInLanguage,
                    }
                })
            };
        });
        await topicData.fetchAndUpdateTopicData({gates}, this.amplitude_callsite);
    }

    async updateEmailGateRequireEmailVerification() {
        const gates = topicData.topic?.gates?.map((gate) => {
            const gateEmail = (gate as CreateTopicEmailGateRequest);
            return {
                ...gate,
                ...(isDefined(gateEmail.Email) && {
                    Email: {
                        ...gateEmail.Email,
                        require_verification: this.requireEmailVerification,
                    }
                })
            };
        });
        await topicData.fetchAndUpdateTopicData({gates}, this.amplitude_callsite);
    }

    async updateEmailGateRequireOptInCheckbox() {
        const gates = topicData.topic?.gates?.map((gate) => {
            const gateEmail = (gate as CreateTopicEmailGateRequest);
            return {
                ...gate,
                ...(isDefined(gateEmail.Email) && {
                    Email: {
                        ...gateEmail.Email,
                        require_opt_in: this.requireOptInCheckbox,
                    }
                })
            };
        });
        await topicData.fetchAndUpdateTopicData({gates}, this.amplitude_callsite);
    }





    static get styles() {
        return css`
            #container {
                flex-direction: column;
                max-height: calc(100vh - 162px);
                overflow: hidden auto;
                box-sizing: border-box;
                margin-bottom: 1rem;
                padding-right: 30px;
            }

            /* width */
            #container::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            #container::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            #container::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .addon-logo__wrapper {
                display: flex;
                flex-direction: column;
                align-items: center;
                max-width: 192px;
                gap: 16px;
                margin: 20px auto 0 auto;
            }

            .addon-logo {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 100px;
                height: 100px;
                flex-shrink: 0;
                border-radius: 12px;
                background:#F5F4F8;
            }

            .paywall-subtitle {
                margin-top: 0;
                margin-bottom: 0;
                color: var(--theme-input-color);
                font-size: 12px;
            }

            .addon-logo__label {
                color: #454056;
                text-align: center;
                font-size: 18px;
                font-weight: 700;
                line-height: 150%;
                margin: 0;
            }

            .checkbox-wrapper {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: flex-start;
                gap: 2px;
                line-height: 120%;
                color: rgb(16, 44, 61);
                margin-top: 5px;
            }

            .opt-in-text {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 2px;
                margin: 10px 0;
            }


            .main-wrapper {
                display: flex;
                width: 100%;
                flex-direction: column;
                align-items: flex-start;
            }

            .gated-access__tabs {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 100px;
                border: 1px solid #102C3D;
            }

            .gated-access__tab {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 8px;
                background: transparent;
                padding: 5px 10px;
                cursor: pointer;
                color: #091E2C;
                text-align: center;
                font-family: Roboto;
                font-weight: 500;
                line-height: 20px;
                letter-spacing: 0.1px;
            }

            .gated-access__tab.right {
                border-radius: 0px 100px 100px 0px;
            }

            .gated-access__tab.left {
                border-radius: 100px 0px 0px 100px;
            }

            .gated-access__tab.active {
                background: #102C3D;
                color: #F9EFEC;
            }

            .tab-state {
                display: flex;
                padding: 5px;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                border-radius: 2px;
                background: #666276;
                color: #FFF;
                text-align: center;
                font-size: 10px;
                line-height: 120%;
            }

            .paragraph_link {
                color: var(--main-primary-color);
                text-decoration: underline;
            }

            .tab-state.active {
                background: #5EA667;
            }

            .title {
                color: #000;
                text-align: center;
                font-size: 18px;
                font-weight: 700;
                line-height: 130%;
                margin: 0;
            }

            .description {
                color: #091E2C;
                font-size: 16px;
                line-height: 150%;
                margin: 0;
            }

            .info-paragraph {
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .setting-switch {
                align-items: center;
                display: flex;
                gap: 8px;
                margin: 0.5rem auto;
            }

            .switch-label {
                text-align: left;
            }

            .switch-label.left {
                text-align: right
            }

            .sub-title {
                margin-bottom: 0px;
                color: var(--theme-input-color);
            }

            .item-list {
                width: 100%;
                display: flex;
                flex-direction: column;
                align-items: flex-start;
            }

            .assign-preview-points {
                width: 100%;
                line-height: 15px;
                border-radius: 8px;
                margin: 15px 0;
                border: 1px solid var(--main-primary-color);
                --mdc-theme-on-primary: var(--main-primary-color);
                --mdc-theme-primary: transparent;
                --mdc-button-height: 50px;
                --mdc-typography-button-font-size: 12px;
                --mdc-typography-button-text-transform: capitalize;
            }

            .item-list__row {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 10px 0;
                gap: 14px;
                border-bottom: 1px solid #666276;
            }

            .item-row__status {
                flex: 1;
                text-align: center;
            }

            .item-row__name {
                flex: 3;
                text-align: center;
            }

            .item-row__copy {
                flex: 2;
                text-align: center;
            }

            .item-row__edit {
                flex: 2;
                text-align: center;
            }

            .item-row__uses {
                flex: 2;
                text-align: center;
            }

            .item-row__header {
                color: #443F56;
                text-align: center;
                font-weight: 500;
                display:inline-flex;
            }

            .item-row__action {
                --mdc-icon-size: 24px;
                cursor: pointer;
            }

            .action-buttons {
                width: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 1rem;
            }

            .item-add-button {
                width: 100%;
                display: flex;
                justify-content: left;
                align-items: flex-start;
                gap: 4px;
            }

            .item-add__icon {
                --mdc-icon-size: 20px;
            }

            .item-add__label {
                color: #666276;
                text-decoration: underline;
                cursor: pointer;
            }

            .buttons-wrapper {
                width: 100%;
                display: flex;
                padding: 8px 0px;
                justify-content: right;
                align-items: right;
                gap: 10px;
            }

            .edit__textfield {
                width: 100%;
            }

            .add-price__label {
                color: #091E2C;
            }

            .add-price__wrapper {
                width: 100%;
                display: flex;
                align-items: flex-start;
                justify-content: center;
                gap: 14px;
            }

            .input-price {
                flex: 1;
            }

            .add-price__currency {
                width: 100px;
            }

            .select-points {
                --mdc-theme-primary: var(--main-primary-color);
                --mdc-button-outline-color: var(--main-primary-color);
                --mdc-typography-button-text-transform: case;
            }

            .addon__footer {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                padding-right: 30px;
                gap: 10px;
            }
        `;
    }
}